import * as React from 'react';
import { geolocated, GeolocatedProps } from 'react-geolocated';
import { apiBaseUrl } from '../api';
import Timer from '../components/Timer';
import Loader from '../components/Loader';

type AppStateError = 'unknown-state';

const FetchErrors = {
  missingGEOcoordinates: 'missing-geo-coordinates',
  noActiveSlot: 'no-active-slot',
  requestError: 'request-error',
  serverError: 'server-error'
};

interface IState {
  error: AppStateError | null;
  isLoading: boolean;
  name: string;
  activeSlotId: number | null;
  serverDuration: number;
  localStartTime: Date;
}

class Track extends React.Component<GeolocatedProps, IState> {
  constructor(props: any) {
    super(props);
    this.state = {
      activeSlotId: null,
      error: null,
      isLoading: false,
      name: localStorage.getItem('name') || '',
      serverDuration: 0,
      localStartTime: new Date()
    };
  }

  componentWillMount() {
    this.fetchActiveSlot();
  }

  render() {
    const { error } = this.state;
    return (
      <div className="textContent">
        {
          error
            ? this.renderError()
            : this.renderContent()
        }
      </div>
    );
  }

  renderContent(): JSX.Element {
    const {
      name,
      activeSlotId,
      serverDuration,
      localStartTime,
      isLoading
    } = this.state;
    let q = '';
    if (name) {
      q = `?name=${encodeURI(name)}`
    }
    return (
      <div style={{ textAlign: 'center' }}>
        <div className="subNav">
          <a
            href={`/time/list${q}`}
          >My Time</a>
        </div>
        <h1
          style={{
            fontWeight: 'normal',
            margin: 0,
            marginBottom: 37,
            marginTop: 37,
            padding: 0
          }}
        >
          Time Tracking
        </h1>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <div
            placeholder="Name"
            style={{
              fontSize: '120%',
              height: 37,
              marginBottom: 37,
              marginLeft: 25,
              marginRight: 25,
              maxWidth: 190,
              textAlign: 'center',
              width: '100%',
            }}
          >for {name}</div>
          {activeSlotId && (
            <React.Fragment>
              <div style={{ marginBottom: 15 }}>Time tracking is active!</div>
              <div style={{ marginBottom: 15 }}>
                <Timer
                  serverDuration={serverDuration}
                  localStartTime={localStartTime}
                />
              </div>
            </React.Fragment>
          )}
          {isLoading ? <Loader /> :
            <button
              style={{
                fontSize: '120%',
                height: 37,
                marginLeft: 20,
                marginRight: 20,
                maxWidth: 200,
                width: '100%'
              }}
              onClick={activeSlotId ? this.updateTimeSlot : this.startTimeSlot}
            >
              {activeSlotId ? 'Stop' : 'Start'}
            </button>
          }
        </div>
      </div >
    );
  }

  renderError(): JSX.Element {
    return (
      <React.Fragment>
        <h1>Ooops!</h1>
        <p>Something went wrong. Failed to fetch state.</p>
        <button onClick={this.reloadPage}>Retry</button>
      </React.Fragment>
    );
  }

  fetchActiveSlot = async () => {
    try {
      const { name } = this.state;
      this.setState({ isLoading: true });
      const response = await fetch(
        `${apiBaseUrl}time/activeSlot?user=${encodeURI(name)}`
      );
      validateResponse(response);
      const data = await response.json();
      this.setState({
        activeSlotId: data.slotId || null,
        serverDuration: data.duration,
        localStartTime: new Date(),
        error: null,
        isLoading: false
      });
    } catch (error) {
      this.setState({
        error: 'unknown-state',
        isLoading: false
      });
    }
  };

  startTimeSlot = async () => {
    try {
      const { coords } = this.props;
      if (!coords) {
        throw new Error(FetchErrors.missingGEOcoordinates);
      }
      this.setState({ isLoading: true });
      const { name } = this.state;
      const locationUri = encodeURI(`${coords.latitude},${coords.longitude}`);
      const userUri = encodeURI(name);
      const url = `${apiBaseUrl}time/startSlot?user=${userUri}&location=${locationUri}`;
      const response = await fetch(url);
      validateResponse(response);
      const responseJson = await response.json();
      const { slotId } = responseJson;
      if (!slotId) {
        throw FetchErrors.requestError;
      }
      this.setState({
        activeSlotId: slotId,
        localStartTime: new Date(),
        serverDuration: 0,
        isLoading: false
      });
    } catch (error) {
      switch (error.message) {
        case FetchErrors.missingGEOcoordinates:
          alert('Failed to start time tracking. Missing GEO coordinates.');
          break;
        case FetchErrors.requestError:
          alert('Request failed.');
          break;
        case FetchErrors.serverError:
          alert('Server error.');
          break;
        default:
          alert('Unexpected error.');
          break;
      }
      this.setState({ isLoading: false });
    }
  };

  updateTimeSlot = async () => {
    try {
      const { coords } = this.props;
      const { activeSlotId } = this.state;
      if (!coords) {
        throw new Error(FetchErrors.missingGEOcoordinates);
      }
      if (!activeSlotId) {
        throw new Error(FetchErrors.noActiveSlot);
      }
      const locationUri = encodeURI(`${coords.latitude},${coords.longitude}`);
      const url = `${apiBaseUrl}time/updateSlot?slotId=${activeSlotId}&location=${locationUri}`;
      const response = await fetch(url);
      validateResponse(response);
      this.setState({
        activeSlotId: null,
        isLoading: false
      });
    } catch (error) {
      switch (error.message) {
        case FetchErrors.missingGEOcoordinates:
          alert('Failed to end time tracking. Missing GEO coordinates.');
          break;
        case FetchErrors.noActiveSlot:
          alert('No active tracking');
          break;
        case FetchErrors.requestError:
          alert('Request failed.');
          break;
        case FetchErrors.serverError:
          alert('Server error.');
          break;
        default:
          alert('Unexpected error.');
          break;
      }
      this.setState({ isLoading: false });
    }
  };

  reloadPage = () => {
    window.location.reload(true);
  };
}

function validateResponse(response: Response) {
  if (response.status !== 200) {
    if (response.status >= 500) {
      throw FetchErrors.serverError;
    } else {
      throw FetchErrors.requestError;
    }
  }
}

export default geolocated()(Track);



// WEBPACK FOOTER //
// ./src/time/Track.tsx